import { MarkerClusterer, SuperClusterAlgorithm } from "@googlemaps/markerclusterer";
import { AdvancedMarker, Pin, useMap } from "@vis.gl/react-google-maps";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { calculateScaledRadius } from "@/common/utils/math.util";
import { useCatalogSelectionUrlParams } from "@/utils/customHook.util";
import type { MapPinDataArray } from "./MapPinData";

type ClusteredMapPinsProps = {
    pins: MapPinDataArray;
    onPinHover: (pinId: string | undefined) => void;
    highlightedPinId: string | undefined;
};

export function ClusteredMapPins({
    pins,
    onPinHover,
    highlightedPinId,
}: ClusteredMapPinsProps) {
    const map = useMap();
    const clusterer = useMemo(() => {
        if (!map) return null;
        return new MarkerClusterer({
            map,
            renderer: {
                render: ({
                    count,
                    position,
                }: {
                    count: number;
                    position: google.maps.LatLng;
                }) => {
                    const countText = count > 100 ? "100+" : count;

                    const radius = calculateScaledRadius(
                        count,
                        MIN_CLUSTER_RADIUS,
                        MAX_CLUSTER_RADIUS,
                    );

                    const svgSize = radius * 2;
                    const center = svgSize / 2;

                    const svg = `
                    <svg xmlns="http://www.w3.org/2000/svg" width="${svgSize}" height="${svgSize}" viewBox="0 0 ${svgSize} ${svgSize}">
                        <circle cx="${center}" cy="${center}" r="${radius}" fill="#000000" stroke="none" />
                        <text x="${center}" y="${center}" text-anchor="middle" dominant-baseline="central"
                              fill="#ffffff" font-size="16" font-weight="bold">${countText}</text>
                    </svg>
                `;

                    const clusterDiv = document.createElement("div");
                    clusterDiv.innerHTML = svg;

                    return new google.maps.marker.AdvancedMarkerElement({
                        position,
                        content: clusterDiv,
                        zIndex: 999,
                    });
                },
            },
            // This is using a different radius than the SVG radius as this controls the clustering algorithm
            // and the area from which markers are considered to be in the same cluster. The SVG radius is used to
            // control the size of the cluster icon.
            algorithm: new SuperClusterAlgorithm({ radius: CLUSTER_AREA_RADIUS }),
        });
    }, [map]);

    const [markers, setMarkers] = useState<{
        [key: string]: google.maps.marker.AdvancedMarkerElement;
    }>({});

    // Set marker ref callback
    const setMarkerRef = useCallback(
        (marker: google.maps.marker.AdvancedMarkerElement | null, key: string) => {
            setMarkers((markers) => {
                const isAddingMarker = !!marker;
                const hasMarker = !!markers[key];

                // No change needed if neither adding nor removing
                if (!isAddingMarker && !hasMarker) {
                    return markers;
                }

                // No change needed if trying to add when already exists
                if (isAddingMarker && hasMarker) {
                    return markers;
                }

                // Add new marker
                if (isAddingMarker) {
                    return { ...markers, [key]: marker };
                }

                // Remove existing marker
                const { [key]: _, ...newMarkers } = markers;
                return newMarkers;
            });
        },
        [],
    );

    useEffect(() => {
        if (!clusterer) return;

        clusterer.clearMarkers();
        clusterer.addMarkers(Object.values(markers));
    }, [clusterer, markers]);

    return pins.map((pin) => (
        <ClusteredPin
            key={pin.id}
            pin={pin}
            highlightedPinId={highlightedPinId}
            onPinHover={onPinHover}
            setMarkerRef={setMarkerRef}
        />
    ));
}

type ClusteredPinProps = {
    pin: MapPinDataArray[0];
    highlightedPinId: string | undefined;
    onPinHover: (pinId: string | undefined) => void;
    setMarkerRef: (
        marker: google.maps.marker.AdvancedMarkerElement | null,
        key: string,
    ) => void;
};

function ClusteredPin({
    pin,
    highlightedPinId,
    onPinHover,
    setMarkerRef,
}: ClusteredPinProps) {
    const { urlState, setUrlState } = useCatalogSelectionUrlParams();
    const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);

    const handleClick = useCallback(() => {
        setUrlState({ catalogId: pin.id });
    }, [setUrlState, pin.id]);

    const handleMouseEnter = useCallback(() => {
        onPinHover(pin.id);
    }, [onPinHover, pin.id]);

    const handleMouseLeave = useCallback(() => {
        onPinHover(undefined);
    }, [onPinHover]);

    const ref = useCallback(
        (marker: google.maps.marker.AdvancedMarkerElement | null) => {
            setMarkerRef(marker, pin.id);
            markerRef.current = marker;
        },
        [setMarkerRef, pin.id],
    );

    return (
        <AdvancedMarker
            position={pin}
            onClick={handleClick}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            ref={ref}
        >
            <Pin
                background={
                    highlightedPinId === pin.id && urlState.searchCatalogId !== pin.id
                        ? "var(--a-ds-color-n-50)"
                        : "var(--a-ds-font-color-primary)"
                }
                glyphColor="var(--a-ds-color-n-100)"
                borderColor={
                    highlightedPinId === pin.id && urlState.searchCatalogId !== pin.id
                        ? "var(--a-ds-color-n-50)"
                        : "var(--a-ds-font-color-primary)"
                }
                scale={
                    urlState.searchCatalogId === pin.id
                        ? 1.5
                        : highlightedPinId === pin.id
                          ? 1.2
                          : 1
                }
            />
        </AdvancedMarker>
    );
}

const MIN_CLUSTER_RADIUS = 25;
const MAX_CLUSTER_RADIUS = 65;
const CLUSTER_AREA_RADIUS = 150;
